import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { Icd10TreeAction } from '@lib/insurance-packages/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

/**
 * One МКХ-10 code with its name. With an `action`, the row gets a checkbox and — while nothing is selected — a
 * remove or add button; a code already in the list is marked as added instead. Indentation is the host's.
 */
@Component({
  selector: 'insurance-packages-icd10-code-row',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe],
  host: { class: 'flex items-start gap-3 border-b border-neutral-100 py-2 pr-3.5' },
  template: `
    @if (action()) {
      <input
        class="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary-600 disabled:cursor-default disabled:opacity-40"
        type="checkbox"
        [attr.aria-label]="'packageConfigurator.action.selectDiagnoses' | translate"
        [checked]="selected()"
        [disabled]="present()"
        (change)="selectedToggle.emit()" />
    }
    <span class="w-14 shrink-0 font-bold text-neutral-500">{{ code() }}</span>
    <span class="min-w-0 flex-1 text-neutral-400">{{ name() }}</span>
    @if (action() === 'add' && present()) {
      <span class="shrink-0 rounded-full bg-success-50 px-2 py-0.5 text-xs font-semibold text-success-700">{{ 'packageConfigurator.label.diagnosisInList' | translate }}</span>
    } @else if (action() === 'add' && actionable()) {
      <button
        class="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-primary-700"
        type="button"
        [matTooltip]="'packageConfigurator.action.addDiagnosis' | translate"
        (click)="add.emit()">
        <mat-icon class="mt-1 text-sm">add</mat-icon>
      </button>
    } @else if (action() === 'remove' && actionable()) {
      <button
        class="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-error-200 bg-error-50 text-error-700"
        type="button"
        [matTooltip]="'packageConfigurator.action.removeDiagnosis' | translate"
        (click)="remove.emit()">
        <mat-icon class="mt-1 text-sm">delete</mat-icon>
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Icd10CodeRowComponent {
  readonly code = input.required<string>();
  readonly name = input<string>('');
  readonly action = input<Icd10TreeAction | null>(null);
  readonly selected = input<boolean>(false);
  /** Row buttons give way to a bulk action while anything is selected. */
  readonly actionable = input<boolean>(true);
  /** Already in the list — only meaningful when adding. */
  readonly present = input<boolean>(false);

  readonly selectedToggle = output<void>();
  readonly remove = output<void>();
  readonly add = output<void>();
}
